// PeriodCalculator.js
import { useState } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { SecondAds, FirstAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";
import PeriodEducationalContent from "./PeriodEducationalContent";
const TITLE = "Kalkulator Siklus Menstruasi - Hitung Tanggal Menstruasi Berikutnya dengan Akurat";
const DESCRIPTION = "Kalkulator siklus menstruasi akurat untuk menghitung tanggal menstruasi berikutnya dan hari ovulasi. Masukkan tanggal haid terakhir untuk mengetahui tanggal menstruasi berikutnya dan masa subur untuk 12 bulan ke depan. Kalkulator siklus haid gratis dengan kalender lengkap dan penjelasan detail.";
const KEYWORDS = "kalkulator siklus menstruasi, hitung siklus menstruasi, kalkulator siklus haid, hari ovulasi, tanggal menstruasi berikutnya, kalender siklus menstruasi, hitung hari subur, period calculator, kalkulator kebiasaan bulanan, kapan haid datang, hitung menstruasi, hari ovulasi";
const OG_URL = "https://do-calculate.com/calculator/id/period/";

const GREGORIAN_MONTHS = [ "Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember" ];
function PeriodCalculator() {
  const [dateInput, setDateInput] = useState({
    day: new Date().getDate(),
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear()
  });

  const [periodLength, setPeriodLength] = useState(5);
  const [cycleLength, setCycleLength] = useState(28);
  const [lastPeriodError, setLastPeriodError] = useState("");
  const [periodLengthError, setPeriodLengthError] = useState("");
  const [cycleLengthError, setCycleLengthError] = useState("");
  const [result, setResult] = useState(null);

  const addDays = (date, days) => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  };

  const formatDate = (date) => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const handleDropdownChange = (e) => {
    const { name, value } = e.target;
    const newDateInput = { ...dateInput, [name]: value };
    setDateInput(newDateInput);

    const selectedDate = new Date(newDateInput.year, newDateInput.month - 1, newDateInput.day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selectedDate > today) {
      setLastPeriodError("Tanggal harus di masa lalu");
    } else {
      const diffTime = Math.abs(today - selectedDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      if (diffDays > 90) {
        setLastPeriodError("Tanggal tampak terlalu jauh di masa lalu");
      } else {
        setLastPeriodError("");
      }
    }
  };

  const handlePeriodLengthChange = (event) => {
    const value = event.target.value;
    if (value !== "") {
      const parsedValue = parseInt(value);
      if (!isNaN(parsedValue) && parsedValue >= 1 && parsedValue <= 10) {
        setPeriodLength(parsedValue);
        setPeriodLengthError("");
      } else {
        setPeriodLength(value);
        if (isNaN(parsedValue)) {
          setPeriodLengthError("Pastikan memasukkan angka yang valid");
        } else if (parsedValue < 1) {
          setPeriodLengthError("Durasi haid harus minimal 1 hari");
        } else if (parsedValue > 10) {
          setPeriodLengthError("Durasi haid tidak boleh lebih dari 10 hari");
        }
      }
    } else {
      setPeriodLength("");
      setPeriodLengthError("");
    }
  };

  const handleCycleLengthChange = (event) => {
    const value = event.target.value;
    if (value !== "") {
      const parsedValue = parseInt(value);
      if (!isNaN(parsedValue) && parsedValue >= 21 && parsedValue <= 35) {
        setCycleLength(parsedValue);
        setCycleLengthError("");
      } else {
        setCycleLength(value);
        if (isNaN(parsedValue)) {
          setCycleLengthError("Pastikan memasukkan angka yang valid");
        } else if (parsedValue < 21) {
          setCycleLengthError("Panjang siklus harus minimal 21 hari");
        } else if (parsedValue > 35) {
          setCycleLengthError("Panjang siklus tidak boleh lebih dari 35 hari");
        }
      }
    } else {
      setCycleLength("");
      setCycleLengthError("");
    }
  };

  const handleReset = () => {
    setDateInput({
      day: new Date().getDate(),
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear()
    });
    setPeriodLength(5);
    setCycleLength(28);
    setLastPeriodError("");
    setPeriodLengthError("");
    setCycleLengthError("");
    setResult(null);
  };

  const calculatePeriod = (e) => {
    e.preventDefault();
    const startDate = new Date(dateInput.year, dateInput.month - 1, dateInput.day);

    if (isNaN(startDate.getTime()) || !periodLength || !cycleLength || lastPeriodError) {
      return;
    }

    const periods = [];
    for (let i = 0; i < 12; i++) {
      const periodStart = addDays(startDate, cycleLength * i);
      const periodEnd = addDays(periodStart, periodLength - 1);
      const ovulationDay = addDays(periodStart, cycleLength - 14);
      const ovulationStart = addDays(ovulationDay, -2);
      const ovulationEnd = addDays(ovulationDay, 2);
      periods.push({
        cycleNumber: i + 1,
        periodStart: formatDate(periodStart),
        periodEnd: formatDate(periodEnd),
        ovulationStart: formatDate(ovulationStart),
        ovulationEnd: formatDate(ovulationEnd),
        ovulationDay: formatDate(ovulationDay),
      });
    }
    setResult({
      periods: periods,
      cycleLength: cycleLength,
      periodLength: periodLength,
    });
  };

  const generateSchemaData = () => {
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Halaman Utama",
          item: "https://do-calculate.com/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Kalkulator",
          item: "https://do-calculate.com/id/all-calculators/",
        },
        {
          "@type": "ListItem",
          position: 3,
          name: "Kalkulator Siklus Menstruasi",
          item: OG_URL,
        },
      ],
    };
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "Bagaimana cara menghitung tanggal menstruasi berikutnya?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Untuk menghitung tanggal menstruasi berikutnya, masukkan tanggal hari pertama haid terakhir dan rata-rata panjang siklus Anda (biasanya 28 hari). Tambahkan jumlah hari siklus untuk menentukan tanggal menstruasi berikutnya.",
          },
        },
        {
          "@type": "Question",
          name: "Berapa panjang siklus menstruasi normal?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Panjang siklus menstruasi normal berkisar antara 21 hingga 35 hari, dengan rata-rata 28 hari. Sedangkan durasi haid berkisar antara 3 hingga 7 hari.",
          },
        },
        {
          "@type": "Question",
          name: "Kapan ovulasi terjadi?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Ovulasi biasanya terjadi 14 hari sebelum tanggal menstruasi berikutnya. Masa subur berlangsung dari 5 hari sebelum ovulasi hingga 1 hari setelahnya.",
          },
        },
        {
          "@type": "Question",
          name: "Apa saja tanda-tanda mendekati menstruasi?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Tanda-tanda mendekati menstruasi meliputi: nyeri perut bagian bawah, perubahan suasana hati, kembung, nyeri payudara, kelelahan, perubahan nafsu makan, dan munculnya jerawat.",
          },
        },
      ],
    };
    const calculatorSchema = {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: "Kalkulator Siklus Menstruasi",
      description: DESCRIPTION,
      url: OG_URL,
      applicationCategory: "HealthApplication",
      operatingSystem: "Any",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "USD",
      },
    };
    return [breadcrumbSchema, faqSchema, calculatorSchema];
  };
  return (
    <div className="ltr" >
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={generateSchemaData()}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ padding: "15px", borderRadius: "12px" }}
          >
            <h1 style={{ fontSize: "22px", margin: 0 }}>{TITLE}</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                <strong>Kalkulator Siklus Menstruasi</strong> adalah alat gratis dan akurat
                untuk menghitung <strong>tanggal menstruasi berikutnya</strong> dan
                <strong>hari ovulasi</strong> untuk 12 bulan ke depan. Yang Anda
                perlukan adalah memasukkan tanggal hari pertama haid terakhir dan rata-rata panjang
                siklus Anda untuk mendapatkan kalender lengkap yang menunjukkan tanggal
                <strong>menstruasi berikutnya</strong> dan <strong>masa subur</strong>{" "}
                dengan akurat.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />
            <div className="row">
              <div className="col-sm-12 col-md-6 calculatorStyle">
                <div className="p-3 mb-3 bg-success text-white text-center rounded">
                  <h2 style={{ fontSize: "18px", margin: 0 }}>
                    Hitung Tanggal Menstruasi Berikutnya
                  </h2>
                </div>
                <form onSubmit={calculatePeriod}>
                  <div className="form-group mb-3">
                    <label >
                      <strong>Hari pertama haid terakhir:</strong>
                    </label>
                    <div className="d-flex gap-2">
                      <select style={{width:'100px'}} className="form-control" name="day" value={dateInput.day} onChange={handleDropdownChange}>
                        {Array.from({ length: 31 }, (_, i) => i + 1).map(d => <option key={d} value={d}>{d}</option>)}
                      </select>
                      <select style={{width:'100%'}} className="form-control" name="month" value={dateInput.month} onChange={handleDropdownChange}>
                        {GREGORIAN_MONTHS.map((m, i) => <option key={i + 1} value={i + 1}>{i + 1} - {m}</option>)}
                      </select>
                      <select style={{width:'100px'}} className="form-control" name="year" value={dateInput.year} onChange={handleDropdownChange}>
                        {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => <option key={y} value={y}>{y}</option>)}
                      </select>
                    </div>
                    {lastPeriodError && <div className="text-danger mt-1" style={{ fontSize: "14px" }}>{lastPeriodError}</div>}
                  </div>

                  <div className="form-group mb-3">
                    <label  htmlFor="periodLength">
                      <strong>Durasi haid (berapa hari berlangsung?):</strong>
                    </label>
                    <div className="d-flex align-items-center">
                      <input id="periodLength" type="number" className="form-control" value={periodLength} onChange={handlePeriodLengthChange} min="1" max="10" required style={{ maxWidth: "150px" }} />
                      <span style={{ marginRight: "10px", padding: "8px 15px", border: "1px solid #ced4da", borderRadius: "4px", backgroundColor: "#f9f9f9" }}>Hari</span>
                    </div>
                    {periodLengthError && <div className="text-danger mt-1" style={{ fontSize: "14px" }}>{periodLengthError}</div>}
                    <small className="form-text text-muted mt-1">Rata-rata normal: 3-7 hari</small>
                  </div>

                  <div className="form-group mb-4">
                    <label  htmlFor="cycleLength">
                      <strong>Rata-rata panjang siklus (waktu antar periode):</strong>
                    </label>
                    <div className="d-flex align-items-center">
                      <input id="cycleLength" type="number" className="form-control" value={cycleLength} onChange={handleCycleLengthChange} min="21" max="35" required style={{ maxWidth: "150px" }} />
                      <span style={{ marginRight: "10px", padding: "8px 15px", border: "1px solid #ced4da", borderRadius: "4px", backgroundColor: "#f9f9f9" }}>Hari</span>
                    </div>
                    {cycleLengthError && <div className="text-danger mt-1" style={{ fontSize: "14px" }}>{cycleLengthError}</div>}
                    <small className="form-text text-muted mt-1">Rata-rata normal: 28 hari (berkisar antara 21-35 hari)</small>
                  </div>

                  <hr />
                  <div className="d-flex gap-2">
                    <button type="submit" className="btn bg-success text-white" style={{ fontSize: "16px", padding: "10px 30px" }}>Hitung</button>
                    <button type="button" className="btn btn-secondary" onClick={handleReset} style={{ fontSize: "16px", padding: "10px 30px" , marginRight: "10px"}}>Hapus</button>
                  </div>
                </form>
              </div>
              <div className="col-sm-12 col-md-6">
                {result && (
                  <div className="calculatorStyle">
                    <div className="p-3 mb-3 bg-success text-white text-center rounded">
                      <h3 style={{ fontSize: "18px", margin: 0 }}>
                        Kalender Siklus Menstruasi - 12 Bulan ke Depan
                      </h3>
                    </div>
                    <div
                      className="alert alert-info"
                      style={{
                        fontSize: "14px",
                        maxHeight: "500px",
                        overflowY: "auto",
                      }}
                    >
                      {result.periods.map((period, index) => (
                        <div
                          key={index}
                          style={{
                            marginBottom: "20px",
                            padding: "15px",
                            backgroundColor:
                              index % 2 === 0 ? "#f8f9fa" : "#ffffff",
                            borderRadius: "8px",
                            border: "1px solid #dee2e6",
                          }}
                        >
                          <h4
                            style={{
                              fontSize: "16px",
                              color: "#198754",
                              marginBottom: "10px",
                            }}
                          >
                            <strong>Siklus ke-{period.cycleNumber}</strong>
                          </h4>
                          <div style={{ lineHeight: "1.8" }}>
                            <div style={{ marginBottom: "5px" }}>
                              <strong>Masa Haid:</strong>{" "}
                              {period.periodStart} - {period.periodEnd}
                            </div>
                            <div style={{ marginBottom: "5px" }}>
                              <strong>Hari Ovulasi:</strong>{" "}
                              {period.ovulationDay}
                            </div>
                            <div
                              style={{
                                color: "#0d6efd",
                                fontWeight: "500",
                              }}
                            >
                              <strong>Masa Subur:</strong>{" "}
                              {period.ovulationStart} - {period.ovulationEnd}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <hr />
            <div
              className="alert alert-info"
              style={{ fontSize: "15px", lineHeight: "1.8" }}
            >
              <strong>💡 Catatan Penting:</strong> Hasil ini bersifat perkiraan dan mengasumsikan
              siklus Anda teratur. Untuk informasi akurat tentang kesuburan dan ovulasi,
              disarankan berkonsultasi dengan dokter atau menggunakan tes ovulasi rumahan.
            </div>
            <hr />
            <div className="row">
              <RelatedLinks />
            </div>
          </div>
        </div>
        <div className="col-sm-12 col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>
      <PeriodEducationalContent />
      <br />
    </div>
  );
}
export default PeriodCalculator;